/**
 * Le passage d'appareil, en hot-seat — le dernier morceau du lot 6.
 *
 * Sans lui, un téléphone posé au milieu de la table change de main sans que personne
 * ne sache quand : le joueur suivant apprend que c'était à lui parce que les autres
 * le lui disent. Pire, n'importe qui peut jouer à la place d'un autre sans s'en
 * apercevoir — l'appareil accepte tous les humains, c'est le principe même du
 * hot-seat, et rien à l'écran ne dit à qui il appartient à cet instant.
 *
 * ## Ce que le rideau fait, et ce qu'il ne fait pas
 *
 * Il ne cache **rien**. Au 421 toute l'information est publique : les jets posés sont
 * affichés à toute la table, c'est même un point de règle du § 3.3. Le rideau ne
 * protège pas un secret, il **marque un changement de main**.
 *
 * D'où trois refus, et chacun corrige un cas où il serait pénible :
 *
 * - **jamais quand un seul humain joue** — solo et campagne n'ont pas de passage ;
 * - **jamais pendant qu'une IA joue** — personne ne prend le téléphone pour regarder
 *   Marcel réfléchir ;
 * - **jamais pendant qu'un jet roule** — le rideau tomberait sur des dés en l'air, et
 *   le suivant prendrait l'appareil sans avoir vu le lancer du précédent.
 */

import { aQuiDeJouer, poseForcee, type Session } from "./session.js";

export interface EtatPassage {
  /** Le siège humain qui tient l'appareil, ou `null` si personne ne l'a encore pris. */
  readonly enMain: number | null;
  /** Le siège à qui l'on demande de le prendre, ou `null` si le jeu suit son cours. */
  readonly attendu: number | null;
}

export function passageNeuf(): EtatPassage {
  return { enMain: null, attendu: null };
}

/**
 * Met à jour l'état du passage au vu de la session courante.
 *
 * `humains` est le nombre de joueurs humains autour de l'appareil : en dessous de
 * deux, il n'y a pas de passage à organiser, et le rideau ne s'affiche jamais.
 */
export function suivrePassage(
  etat: EtatPassage, session: Session, humains: number,
): EtatPassage {
  const tour = aQuiDeJouer(session.partie);

  if (humains < 2) {
    // Solo et campagne : l'appareil ne quitte pas la main de son propriétaire. On le
    // lui met d'office plutôt que de laisser `enMain` à null — sans quoi
    // `appareilEnMain` répondrait « non » à quelqu'un qui tient pourtant le téléphone,
    // et l'écran refuserait toutes ses actions.
    if (tour && !tour.estIA) return { enMain: tour.siege, attendu: null };
    return { enMain: etat.enMain, attendu: null };
  }

  // Un jet roule : on laisse l'animation finir. Le rideau attendra le prochain état.
  if (session.demande !== null) return { ...etat, attendu: null };

  if (!tour || tour.estIA) return { ...etat, attendu: null };
  // On ne fait pas passer le téléphone à quelqu'un qui n'a rien à décider : son
  // dernier lancer est consommé, sa main se pose toute seule. Le rideau
  // apparaîtrait et disparaîtrait aussitôt, pour rien.
  if (poseForcee(session)) return { ...etat, attendu: null };
  if (etat.enMain === tour.siege) return { ...etat, attendu: null };
  return { ...etat, attendu: tour.siege };
}

/** Le joueur a pris l'appareil : le rideau se lève. */
export function prendreLAppareil(etat: EtatPassage): EtatPassage {
  if (etat.attendu === null) return etat;
  return { enMain: etat.attendu, attendu: null };
}

/** Vrai si ce siège tient l'appareil et peut donc agir. */
export function appareilEnMain(etat: EtatPassage, siege: number): boolean {
  return etat.attendu === null && etat.enMain === siege;
}
